import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map, shareReplay } from 'rxjs';
import { ENVIRONMENT } from '../../../../environment/environment';
import { SystemSettingConfig, SystemSettingConfigApiResponse } from '../../models/system-setting.model';
import { StorageService } from '../storage.service';

/**
 * Client for the backend's system-setting configuration endpoints for this channel.
 *
 * Named after the backing store rather than any one setting: every value it reads is a row in the
 * backend `SystemSetting` table, served by `api/v1/configuration/*`. Today that is the askPOD
 * feature toggle; further settings belong here rather than in a new service per flag.
 *
 * **Why this exists:** values in `environment.*.ts` are compiled into the bundle, so switching a
 * feature off would mean a rebuild and redeploy. These settings live in the backend `SystemSetting`
 * table and are changed with a SQL statement against the target database, which takes effect
 * without shipping anything.
 *
 * **Authenticated:** the endpoint requires a Client bearer token, so — unlike
 * `RecaptchaConfigService` — this deliberately does NOT pass `noToken`. Callers must only invoke it
 * for a signed-in user; an anonymous call would 401 and resolve to the fail-closed fallback.
 *
 * **Single request per signed-in user:** the result is cached with `shareReplay(1)`, so the header
 * (which is instantiated per layout) and any future consumer share one HTTP call. This deliberately
 * differs from `RecaptchaConfigService`, which re-fetches every time because QA flips that toggle
 * mid-session.
 *
 * **The cache is keyed by user id, and must stay that way.** These flags are per-user: with the
 * backend key set to `allowlist`, askPOD is visible only to users named in `AskpodAllowList`. Logout
 * navigates by router rather than reloading the page, so this root-provided singleton survives an
 * account switch — a cache held for the app's lifetime would replay the previous account's answer.
 *
 * **Staleness:** a flipped flag reaches an already-open tab only on the next page load, and the
 * backend's own cache adds up to its TTL on top. That is the intended trade-off; the alternative is
 * polling every client forever.
 *
 * **SSR:** intended to be called from the browser only. The header's `ngOnInit` returns early on the
 * server, so no request is issued during SSR and no server-to-API dependency is introduced into page
 * rendering. Because the server therefore renders with the fallback (hidden), the client's first
 * render must match — see the fail-closed note below.
 *
 * **Error handling (fail-closed):** on any failure the flags resolve to disabled. A kill switch that
 * silently shows the feature whenever the config call fails is not a kill switch. The practical
 * blast radius is small: askPOD needs the same backend to mint its SSO link, so if this call is
 * failing the feature would not work anyway.
 *
 * @see SystemSettingConfig
 * @see RecaptchaConfigService for the deliberately uncached, anonymous counterpart
 */
@Injectable({
    providedIn: 'root',
})
export class SystemSettingService {
    private readonly apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/configuration/features`;

    /**
     * In-flight/completed request shared by all subscribers, for one signed-in user.
     * Created lazily so no request is issued unless something actually reads a flag.
     */
    private config$?: Observable<SystemSettingConfig>;

    /** The user id `config$` was fetched for, so an account switch invalidates it. */
    private cachedForUserId?: string;

    constructor(
        private httpClient: HttpClient,
        private storageService: StorageService,
    ) {}

    /**
     * Returns the system settings for this channel, fetching them at most once per signed-in user.
     *
     * Safe to call repeatedly and from multiple components — later callers replay the cached value
     * rather than triggering another request. Only call this for an authenticated user.
     *
     * @returns Observable that emits the system-setting configuration
     */
    public getConfig(): Observable<SystemSettingConfig> {
        // StorageService.userId is set on login and cleared on logout, so comparing against it
        // invalidates the cache on every logout path — including forced ones (session timeout,
        // device removal) that never run the header's logout handler.
        const userId = this.storageService.userId;

        if (!this.config$ || this.cachedForUserId !== userId) {
            this.cachedForUserId = userId;
            this.config$ = this.fetchConfig().pipe(shareReplay(1));
        }

        return this.config$;
    }

    /**
     * Issues the actual HTTP request.
     *
     * No `noToken` param: the access-token interceptor must attach the bearer token, because the
     * endpoint is behind `[AuthorizeRoles(RoleSystemName.Client)]`.
     *
     * @returns Observable that emits the system-setting configuration
     */
    private fetchConfig(): Observable<SystemSettingConfig> {
        return this.httpClient.get<SystemSettingConfigApiResponse>(this.apiRoute).pipe(
            map((response) => response?.result ?? this.getFallbackConfig()),
            catchError((error) => {
                console.error('Failed to fetch system setting configuration:', error);
                return of(this.getFallbackConfig());
            }),
        );
    }

    /**
     * Returns the fail-closed fallback used when the configuration cannot be read.
     *
     * **Strategy:** everything off. See the class-level note on fail-closed behaviour.
     *
     * @returns A fallback SystemSettingConfig
     */
    private getFallbackConfig(): SystemSettingConfig {
        return {
            isAskPodFeatureEnabled: false,
        };
    }
}
